import { faCalculator, faChartBar } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowLeftCircleFill, Calculator, Clipboard, Clipboard2CheckFill, Clipboard2Data, Clock, CloudDownloadFill, Download, FileCheck, InfoCircleFill, MenuUp, Person, Tools, XCircleFill } from 'react-bootstrap-icons'
import ReactDOM from 'react-dom'
import { useParams } from 'react-router-dom'
import DatePicker from '../../../../components/ui/calendar/date_picket/DatePicker'
import { openDocument } from '../../../../libs/files/openDocument'
import Questionnaire from '../../../../libs/api/services/Questionnaire'
import QuestionnaireAssignment from '../../../../libs/api/services/QuestionnaireAssignment'
import WorkFlow from '../../../../libs/api/services/WorkFlow'
import WorkFlowDMSSection from '../../workflow/management/Sections/WorkFlowDMSSection'
import WorkFlowMessagesSection from '../../workflow/management/Sections/WorkFlowMessagesSection'
import WorkFlowNotesSection from '../../workflow/management/Sections/WorkFlowNotesSection'
import { WorkFlowManagementProvider } from '../../workflow/management/WorkFlowManagementProvider'
import QuestionnaireViewer from '../v2/questionnaireView'


import QuestionnaireStats from './QuestionairreResultComponents/QuestionnaireStats'
import './QuestionnaireResults.css'

// Inline WeightsControl placed in header next to Questions Answered
function WeightsControl({ toolDetails, viewerRef, viewerReadySignal }) {
    const [open, setOpen] = useState(false);

    const [totals, setTotals] = useState({ totalWeight: 0, totalWeightQuestionnaire: 0 });
    const [details, setDetails] = useState([]);
    const [loading, setLoading] = useState(false);
    const wrapperRef = useRef(null);
    const buttonRef = useRef(null);
    // popupStyle removed - popup positioning is computed on open using button rect

    // Update totals/details when toolDetails changes, popup opens, or when viewer signals readiness.
    useEffect(() => {
        let mounted = true;

        async function fetchNow() {
            if (!mounted) return;
            if (!viewerRef || !viewerRef.current) return;
            setLoading(true);
            try {
                const t = await viewerRef.current.calculateTotalWeights();
                const d = await viewerRef.current.getComponentWeightDetails();
                if (mounted) {
                    setTotals(t || { totalWeight: 0, totalWeightQuestionnaire: 0 });
                    setDetails(Array.isArray(d) ? d : []);
                }
            } catch (e) {
                if (mounted) {
                    setTotals({ totalWeight: 0, totalWeightQuestionnaire: 0 });
                    setDetails([]);
                }
            } finally {
                if (mounted) setLoading(false);
            }
        }

        // Only attempt fetch if viewerRef is present or we've received a ready signal
        if ((viewerRef && viewerRef.current) || viewerReadySignal > 0) {
            fetchNow();
        }

        return () => { mounted = false; };
    }, [toolDetails, open, viewerRef, viewerReadySignal]);



    return (
        <div className='d-inline-block' style={{ position: 'relative' }}>
            <button ref={buttonRef} className='btn btn-sm btn-outline-light ms-2' onClick={() => {
                const willOpen = !open;
                if (willOpen && wrapperRef && wrapperRef.current) {
                    const rect = (buttonRef && buttonRef.current) ? buttonRef.current.getBoundingClientRect() : wrapperRef.current.getBoundingClientRect();
                    const spaceBelow = window.innerHeight - rect.bottom;
                    const spaceAbove = rect.top;
                    // spacing and minHeight handled at render time
                    if (spaceBelow < 320 && spaceAbove > spaceBelow) {
                        // open upwards (positioning computed when rendering via portal)
                    } else {
                        // open downwards (positioning computed when rendering via portal)
                    }
                } else if (!willOpen) {
                    // closed
                }
                setOpen(willOpen);
            }}>
                <i className='fas fa-calculator me-1'></i>
                {`${totals.totalWeight} / ${totals.totalWeightQuestionnaire}`}
            </button>
            {open && (() => {
                // Render popup into body via portal to avoid clipping by parent containers
                const rect = (buttonRef && buttonRef.current) ? buttonRef.current.getBoundingClientRect() : null;
                const popupWidth = Math.min(420, Math.max(280, window.innerWidth - 16));
                const margin = 12;
                let style = {
                    position: 'fixed',
                    left: rect ? Math.min(Math.max(rect.left, 8), window.innerWidth - popupWidth - 8) : 8,
                    zIndex: 10000,
                    width: popupWidth,
                    maxWidth: '95vw',
                    boxSizing: 'border-box'
                };
                if (rect) {
                    const spaceBelow = window.innerHeight - rect.bottom;
                    const spaceAbove = rect.top;
                    const headerFooterReserve = 80; // approximate header+footer space
                    if (spaceBelow < 320 && spaceAbove > spaceBelow) {
                        // open upwards
                        const maxH = Math.max(160, spaceAbove - margin - headerFooterReserve);
                        style = { ...style, bottom: (window.innerHeight - rect.top) + margin, maxHeight: `${maxH}px` };
                    } else {
                        const maxH = Math.max(160, spaceBelow - margin - headerFooterReserve);
                        style = { ...style, top: rect.bottom + margin, maxHeight: `${maxH}px` };
                    }
                } else {
                    style = { ...style, top: 80, maxHeight: 'calc(100vh - 160px)' };
                }

                const popup = (
                    <div ref={wrapperRef} style={style}>
                        <div className='card shadow' style={{ display: 'flex', flexDirection: 'column', maxHeight: style.maxHeight }}>
                            <div className='card-header bg-primary-container text-white d-flex justify-content-between align-items-center'>
                                <div><FontAwesomeIcon icon={faChartBar} className='me-2' />Weights Calculation</div>
                                <button className='btn btn-sm btn-outline-light' onClick={() => setOpen(false)}>×</button>
                            </div>
                            {/* Scrollable body */}
                            <div className='card-body p-2' style={{ overflowY: 'auto', flex: 1 }}>
                                {loading ? (
                                    <div className='text-center p-3'>
                                        <div className='spinner-border text-primary' role='status' style={{ width: '1.25rem', height: '1.25rem' }}>
                                            <span className='visually-hidden'>Loading...</span>
                                        </div>
                                        <div className='small mt-2 text-muted'>Calculating...</div>
                                    </div>
                                ) : details && details.length > 0 ? (
                                    <>
                                        {details.map((detail, idx) => (
                                            <div key={detail.componentId} className='mb-2 p-2 border rounded bg-light'>
                                                <div className='fw-bold'>{detail.question || `Component ${idx + 1}`}</div>
                                                <div className='small text-muted'>Type: {detail.componentType}</div>
                                                {detail.selectedOptions.map((opt, oi) => (
                                                    <div key={oi} className='d-flex justify-content-between small p-1 bg-white rounded mt-1'>
                                                        <span className='text-secondary'>{opt.option}</span>
                                                        <span className='fw-bold text-success'>+{opt.weight}</span>
                                                    </div>
                                                ))}
                                                <div className='d-flex justify-content-between fw-bold text-primary mt-2'>
                                                    <span>Subtotal:</span>
                                                    <span className='badge bg-primary-container'>{detail.totalWeight}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                ) : (
                                    <div className='text-center p-3'>No weighted components with answers found.</div>
                                )}
                            </div>
                            {/* Fixed footer with totals - always visible */}
                            <div className='card-footer p-2'>
                                <div className='d-flex justify-content-between align-items-center bg-success-container text-white p-2 rounded fw-bold'>
                                    <span><FontAwesomeIcon icon={faCalculator} className='me-2' />Total:</span>
                                    <span className='badge bg-white text-success'>{totals.totalWeight} / {totals.totalWeightQuestionnaire}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                );

                return ReactDOM.createPortal(popup, document.body);
            })()}
        </div>
    )
}

export default function PatientQuestionnaireResults({ defaultQuestionair, defaultQuestionnaireAssignmentId, type }) {
    const { questionair, QuestionnaireAssignmentId } = useParams()
    const [details, setDetails] = useState()
    const [toolDetails, setToolDetails] = useState()
    const [stats, setStats] = useState()
    const [sideOption, setSideOption] = useState()
    const [downloading, setDownloading] = useState(false)
    const [selectedCompletedDate, setSelectedCompletedDate] = useState()
    const [isSavingCompletedDate, setIsSavingCompletedDate] = useState(false)
    const viewerRef = useRef();
    const [viewerReadySignal, setViewerReadySignal] = useState(0);
    const assignmentId = defaultQuestionnaireAssignmentId ? defaultQuestionnaireAssignmentId : QuestionnaireAssignmentId;

    useEffect(() => {
        setSelectedCompletedDate(toolDetails && toolDetails.completedDate ? toolDetails.completedDate : undefined)
    }, [toolDetails?.completedDate])

    useEffect(() => {
        if (sideOption === 'Stats') {
            Questionnaire
                .Stats(defaultQuestionnaireAssignmentId ? defaultQuestionnaireAssignmentId : QuestionnaireAssignmentId)
                .then((data) => setStats(data))
                .catch(() => { })
        }
    }, [sideOption, defaultQuestionnaireAssignmentId, QuestionnaireAssignmentId])

    const _handleGetReport = () => {
        setDownloading(true)
        Questionnaire
            .Report(defaultQuestionnaireAssignmentId ? defaultQuestionnaireAssignmentId : QuestionnaireAssignmentId)
            .then((data) => {
                openDocument(data.data, data.mimeType, data.fileName)
                setDownloading(false)
            })
            .catch(() => setDownloading(false))
    }

    // loadDetails is exposed so other components (eg. QuestionnaireStats) can refresh
    const loadDetails = useCallback(async () => {
        try {
            const r = await Questionnaire.GetQuestionnaireResultsFull(defaultQuestionnaireAssignmentId ? defaultQuestionnaireAssignmentId : QuestionnaireAssignmentId);
            setDetails(r);
            const wf = await WorkFlow.GetWorkFlowQuestionnaireHistory(r.id).catch(e => console.error(e));
            setToolDetails({ ...r, ...wf });
        } catch (e) {
            console.error(e);
        }
    }, [defaultQuestionnaireAssignmentId, QuestionnaireAssignmentId]);

    useEffect(() => {
        loadDetails();
    }, [loadDetails]);

    const handleCompletedDateChanged = async (value) => {
        if (!assignmentId) return

        setSelectedCompletedDate(value)
        setIsSavingCompletedDate(true)

        try {
            const updatedAssignment = await QuestionnaireAssignment.UpdateCompletedDate(assignmentId, value)
            setToolDetails((previous) => ({
                ...previous,
                ...updatedAssignment,
                completedDate: updatedAssignment && updatedAssignment.completedDate ? updatedAssignment.completedDate : value,
            }))
            setDetails((previous) => previous ? {
                ...previous,
                completedDate: updatedAssignment && updatedAssignment.completedDate ? updatedAssignment.completedDate : value,
            } : previous)
        } catch (e) {
            console.error(e)
        } finally {
            setIsSavingCompletedDate(false)
        }
    }
    // Questionnaire
    //     .GetQuestionnaireResults(questionair)
    //     .then((r) => {
    //         setResults(r)
    //     })

    // const _getQuestion = (res) => {
    //     const rslt = res.answerType.properties.find(q => q.property === "Question")
    //     return rslt ? rslt.values : ''
    // }
    // console.log(toolDetails && toolDetails.questionnaire.resource.resource.sections.filter(a => a.components && a.components.properties && a.components.properties.type === 'Options'))
    // console.log(toolDetails && toolDetails.questionnaire.resource.resource.sections.map(a => a.components.map(z => z.properties)))

    // const questionnaire_props_layout = (unused)
    // _res_1 removed (unused)

    // console.log(toolDetails.questionnaire.resource.resource)
    // console.log(toolDetails.responses)

    // scores computation not currently used

    return (
        <WorkFlowManagementProvider>
            <div className='conatiner text-center q-discover-layout d-flex flex-column h-100'>
                <div className='flex-shrink-1'>
                    {
                        toolDetails
                            ?
                            <div className='text-secondary'>
                                <div className='row bg-secondary-container shadow rounded-bottom p-2'>
                                    <InfoCircleFill size={24} className='text-start m-2' />
                                    <div className='col-4 text-start'>
                                        <div><Clock className='me-1' />Requested On : {toolDetails.createdDate ? toolDetails.createdDate.substring(0, 19) : ''}</div>
                                        <div><Person className='me-1' />Requested By : {toolDetails.assigner ? toolDetails.assigner.firstName : ''} {toolDetails.assigner ? toolDetails.assigner.lastName : ''}</div>
                                    </div>
                                    <div className='col-4 text-start'>
                                        <div className='me-3'><Clock className='me-1' />Assigned Date : {toolDetails.assignedDate ? toolDetails.assignedDate.substring(0, 19) : ''}</div>
                                        <div className='me-3'><Clipboard2CheckFill className='me-1' />Questions Answered : {toolDetails.responses ? toolDetails.responses.length : 0}</div>
                                    </div>
                                    <div className='col-4 text-start'>
                                        <div><Clock className='me-1' />Completed On : {toolDetails.completedDate ? toolDetails.completedDate.substring(0, 19) : ''}</div>
                                        <div className='mt-2'>
                                            <div className='small mb-1'>Adjust completed date</div>
                                            <DatePicker
                                                defaultValue={selectedCompletedDate || toolDetails.completedDate}
                                                onChange={handleCompletedDateChanged}
                                            />
                                            {isSavingCompletedDate ? <div className='small mt-1 text-muted'>Saving completed date...</div> : <></>}
                                        </div>
                                        {/* <div><Calculator className='me-1' />Score : {scores.score} / {scores.total}
                                        </div> */}
                                        <div>
                                            {/* Weights button moved here */}
                                            <Calculator className='me-1' /> Score
                                            <WeightsControl toolDetails={toolDetails} viewerRef={viewerRef} viewerReadySignal={viewerReadySignal} defaultQuestionnaireAssignmentId={defaultQuestionnaireAssignmentId} QuestionnaireAssignmentId={QuestionnaireAssignmentId} />
                                        </div>
                                    </div>

                                    <div>
                                        <div className='d-flex justify-content-end '>
                                            <div className={'ps-1 pb-1 pe-2 w-25 rounded bg-primary-container shadow p-1 m-1 '.concat(sideOption === 'Tools' ? '' : '')}
                                                onClick={(e) => setSideOption('Tools-Open')}
                                                style={{ cursor: 'pointer' }} ><Tools />Tools</div>

                                        </div>
                                    </div>
                                </div>
                            </div>
                            :
                            <></>
                    }
                </div>
                <div className='flex-grow-1 position-relative d-flex flex-column' style={{ overflow: 'auto', minHeight: 0, height: '100%' }}>
                    <div className={'sideOption shadow bg-primary-container rounded rounded-4 '.concat(sideOption === 'Tools-Open' ? 'show' : sideOption === 'Tools' ? 'active' : 'hide')} >
                        <div className='d-flex justify-content-between shadow' onClick={(e) => setSideOption()}>
                            <h3 className='ps-3 m-0 d-flex align-items-center'><Tools size={20} className='me-1' />Tools</h3>
                            <XCircleFill size={30} className='' />
                        </div>
                        <div className='row mb-2 mt-1'>
                            <div className='col-6 '>
                                <div className={'bg-secondary-container d-flex flex-column align-items-center p-2 rounded shadow '.concat(downloading ? 'opacity-75' : '')} style={{ cursor: 'pointer' }} onClick={(e) => { if (!downloading) { } _handleGetReport() }} >{downloading ? <CloudDownloadFill className='me-1' /> : <Download className='me-1' />}{downloading ? 'Generating Report' : 'Download'}</div>
                            </div>
                            <div className='col-6'>
                                <div className={'bg-secondary-container d-flex flex-column align-items-center p-2 rounded shadow'.concat(sideOption === 'Notes' ? 'bg-primary-container shadow' : '')}
                                    onClick={(e) => setSideOption('Notes')}
                                    style={{ cursor: 'pointer' }} ><Clipboard className='me-1' />Notes</div>
                            </div>
                        </div>
                        <div className='row mb-2 mt-1'>
                            <div className='col-6'>
                                <div className={'bg-secondary-container d-flex flex-column align-items-center p-2 rounded shadow'.concat(sideOption === 'Documents' ? 'bg-primary-container shadow' : '')}
                                    onClick={(e) => setSideOption('Documents')}
                                    style={{ cursor: 'pointer' }} ><FileCheck className='me-1' />Documents</div>
                            </div>
                            <div className='col-6'>
                                <div className={'bg-secondary-container d-flex flex-column align-items-center p-2 rounded shadow '.concat(sideOption === 'Messages' ? 'bg-primary-container shadow' : '')}
                                    onClick={(e) => setSideOption('Messages')}
                                    style={{ cursor: 'pointer' }} ><MenuUp className='me-1' />Linked Messages</div>
                            </div>
                        </div>
                        <div className='row '>
                            <div className='col-6'>
                                <div className={'bg-secondary-container d-flex flex-column align-items-center p-2 rounded shadow'.concat(sideOption === 'Stats' ? 'bg-primary-container shadow' : '')}
                                    onClick={(e) => setSideOption('Stats')}
                                    style={{ cursor: 'pointer' }} ><Clipboard2Data className='me-1' />Analyse</div>
                            </div>
                        </div>
                    </div>
                    {
                        <>
                            <div className={'sideOption shadow bg-primary-container rounded rounded-4 '.concat(sideOption === 'Messages' ? 'active' : 'hide')} >
                                <div className='d-flex justify-content-between shadow' >
                                    <h3 className='ps-3 m-0 d-flex align-items-center'><MenuUp size={20} className='me-1' />Linked Messages</h3>
                                    <div>
                                        <ArrowLeftCircleFill onClick={(e) => setSideOption('Tools')} size={30} className='me-1' />
                                        <XCircleFill onClick={(e) => setSideOption('')} size={30} className='' />
                                    </div>
                                </div>
                                <div>
                                    {
                                        toolDetails && toolDetails.messages
                                            ?
                                            <WorkFlowMessagesSection event={toolDetails} messages={toolDetails && toolDetails.messages} />
                                            : <></>
                                    }

                                </div>
                            </div>
                            <div className={'sideOption shadow bg-primary-container rounded rounded-4 '.concat(sideOption === 'Notes' ? 'active' : 'hide')} >
                                <div className='d-flex justify-content-between shadow' >
                                    <h3 className='ps-3 m-0 d-flex align-items-center'><Clipboard size={20} className='me-1' />Notes</h3>
                                    <div>
                                        <ArrowLeftCircleFill onClick={(e) => setSideOption('Tools')} size={30} className='me-1' />
                                        <XCircleFill onClick={(e) => setSideOption('')} size={30} className='' />
                                    </div>
                                </div>
                                {
                                    toolDetails && toolDetails.notes
                                        ?
                                        <WorkFlowNotesSection event={toolDetails} notes={toolDetails && toolDetails.notes} />
                                        // <div className='d-flex flex-column p-1' style={{ height: 'calc(100% - 50px)' }}>
                                        //     <div className='flex-grow-1' style={{ overflow: 'auto' }}>
                                        //         <PatientDMSNotes PatientHistoryId={details.patientHistoryEvent ? details.patientHistoryEvent.id : 0} />
                                        //     </div>
                                        //     <div className='flex-shrink-1'>
                                        //         <PatientAddDMSNoteComponent id={id} event={details.patientHistoryEvent ? details.patientHistoryEvent.id : 0} />
                                        //     </div>
                                        // </div>
                                        : <></>
                                }
                            </div>
                            <div className={'sideOption shadow bg-primary-container rounded rounded-4 '.concat(sideOption === 'Documents' ? 'active' : 'hide')} >
                                <div className='d-flex justify-content-between shadow' >
                                    <h3 className='ps-3 m-0 d-flex align-items-center'><FileCheck size={20} className='me-1' />Documents</h3>
                                    <div>
                                        <ArrowLeftCircleFill onClick={(e) => setSideOption('Tools')} size={30} className='me-1' />
                                        <XCircleFill onClick={(e) => setSideOption('')} size={30} className='' />
                                    </div>
                                </div>
                                {
                                    toolDetails && toolDetails.documents
                                        ?
                                        <WorkFlowDMSSection event={toolDetails} documents={toolDetails && toolDetails.documents} />
                                        // <PatientDMSComponent id={id} event={details.patientHistoryEvent ? details.patientHistoryEvent.id : 0} />
                                        : <></>
                                }
                            </div>
                            <div className={'sideOption shadow bg-primary-container rounded rounded-4 '.concat(sideOption === 'Stats' ? 'active' : 'hide')} >
                                <div className='d-flex justify-content-between shadow' >
                                    <h3 className='ps-3 m-0 d-flex align-items-center'><Clipboard2Data size={20} className='me-1' />Analyse</h3>
                                    <div>
                                        <ArrowLeftCircleFill onClick={(e) => setSideOption('Tools')} size={30} className='me-1' />
                                        <XCircleFill onClick={(e) => setSideOption('')} size={30} className='' />
                                    </div>
                                </div>
                                <div style={{ overflow: 'auto', height: "calc(100% - 30px)" }}>
                                    {
                                        toolDetails && toolDetails.notes
                                            ?
                                            <QuestionnaireStats QuestionnaireAssignmentId={defaultQuestionnaireAssignmentId ? defaultQuestionnaireAssignmentId : QuestionnaireAssignmentId} QuestionnaireId={defaultQuestionair ? defaultQuestionair : questionair} onClose={() => loadDetails()} eventId={toolDetails.patientHistoryEvent ? toolDetails.patientHistoryEvent.id : 0} stats={stats} />
                                            : <></>
                                    }
                                </div>
                            </div>
                        </>
                    }
                    {
                        toolDetails && details
                            ?
                            // V1
                            // <QuestionnaireBuilderProvider assignmentId={defaultQuestionnaireAssignmentId ? defaultQuestionnaireAssignmentId : QuestionnaireAssignmentId}>
                            //     <QCaptureComponentNew
                            //         mode={'Discover'}
                            //         cachedDetails={details}
                            //         QuestionnaireAssignmentId={defaultQuestionnaireAssignmentId ? defaultQuestionnaireAssignmentId : QuestionnaireAssignmentId}
                            //         QuestionnaireId={defaultQuestionair ? defaultQuestionair : questionair}
                            //     />
                            // </QuestionnaireBuilderProvider>
                            // V2
                            <QuestionnaireViewer ref={viewerRef} assignmentId={defaultQuestionnaireAssignmentId ? defaultQuestionnaireAssignmentId : QuestionnaireAssignmentId} onViewerReady={() => setViewerReadySignal(s => s + 1)} />
                            :
                            <></>
                    }

                </div>
            </div>
        </WorkFlowManagementProvider>
    )
    // return (
    //     <div>

    //         <button className='btn btn-sm'>View Documents / Notes</button>
    //         {
    //             results.responses
    //                 ?
    //                 results.responses.map((res) => {
    //                     return (
    //                         <div>
    //                             <div>
    //                                 <b>{_getQuestion(res)}</b>
    //                                 <i>{res.answer}</i>
    //                             </div>
    //                         </div>
    //                     )
    //                 })
    //                 :
    //                 <></>
    //         }
    //     </div>
    // )
}
